import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { AppNotification } from '../types';
import {
  HomeIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  KeyIcon,
  BellIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EyeIcon,
  EyeSlashIcon,
  ShieldCheckIcon,
  AdjustmentsHorizontalIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  BuildingOfficeIcon as BuildingOfficeIconSolid,
  UsersIcon as UsersIconSolid,
  ChartBarIcon as ChartBarIconSolid,
  PresentationChartLineIcon as PresentationChartLineIconSolid,
  AdjustmentsHorizontalIcon as AdjustmentsHorizontalIconSolid,
  EnvelopeIcon as EnvelopeIconSolid,
} from '@heroicons/react/24/solid';

interface LayoutProps {
  children: React.ReactNode;
}

/* ─────────────────────────────────────────────────────────
   Password-strength helper
───────────────────────────────────────────────────────── */
function getStrength(pwd: string): { level: number; label: string; color: string } {
  if (pwd.length === 0) return { level: 0, label: '', color: '' };
  if (pwd.length < 8) return { level: 1, label: 'Trop court', color: 'bg-red-400' };
  if (pwd.length < 10) return { level: 2, label: 'Faible', color: 'bg-orange-400' };
  if (pwd.length < 12) return { level: 3, label: 'Acceptable', color: 'bg-yellow-400' };
  return { level: 4, label: 'Fort', color: 'bg-green-500' };
}

/* ─────────────────────────────────────────────────────────
   ChangePasswordModal
───────────────────────────────────────────────────────── */
const ChangePasswordModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const strength = getStrength(newPassword);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) {
      setError('Le nouveau mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    setIsLoading(true);
    try {
      await apiService.changePassword(currentPassword, newPassword, confirmPassword);
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Une erreur est survenue.');
    } finally {
      setIsLoading(false);
    }
  };

  const PasswordInput: React.FC<{
    label: string;
    value: string;
    onChange: (v: string) => void;
    show: boolean;
    onToggle: () => void;
    placeholder?: string;
  }> = ({ label, value, onChange, show, onToggle, placeholder = '••••••••' }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="block w-full px-4 py-2.5 pr-11 border border-gray-300 rounded-xl text-sm
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
            placeholder-gray-300 transition-shadow"
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
          tabIndex={-1}
        >
          {show ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                <KeyIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-white">Changer mon mot de passe</h2>
                <p className="text-xs text-primary-100">Sécurisez votre compte</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <XMarkIcon className="h-4 w-4 text-white" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {success ? (
            <div className="text-center py-6">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="font-semibold text-gray-800 mb-1">Mot de passe modifié !</p>
              <p className="text-sm text-gray-500 mb-5">Votre mot de passe a été mis à jour avec succès.</p>
              <button
                onClick={onClose}
                className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-xl transition-colors"
              >
                Fermer
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <svg className="h-4 w-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              )}

              <PasswordInput
                label="Mot de passe actuel"
                value={currentPassword}
                onChange={setCurrentPassword}
                show={showCurrent}
                onToggle={() => setShowCurrent((v) => !v)}
              />

              <div>
                <PasswordInput
                  label="Nouveau mot de passe"
                  value={newPassword}
                  onChange={setNewPassword}
                  show={showNew}
                  onToggle={() => setShowNew((v) => !v)}
                />
                {newPassword.length > 0 && (
                  <div className="mt-2">
                    <div className="flex gap-1 mb-1">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                            i <= strength.level ? strength.color : 'bg-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-gray-400">{strength.label}</p>
                  </div>
                )}
              </div>

              <div>
                <PasswordInput
                  label="Confirmer le nouveau mot de passe"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  show={showConfirm}
                  onToggle={() => setShowConfirm((v) => !v)}
                />
                {confirmPassword.length > 0 && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                    </svg>
                    Les mots de passe ne correspondent pas
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-2.5 px-4 border border-gray-300 rounded-xl text-sm font-medium
                    text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 py-2.5 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-xl
                    text-sm font-medium disabled:opacity-50 transition-colors flex justify-center items-center gap-2"
                >
                  {isLoading ? (
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <>
                      <ShieldCheckIcon className="h-4 w-4" />
                      Modifier
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   NotificationPreferencesModal
───────────────────────────────────────────────────────── */
const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}> = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors
      ${checked ? 'bg-primary-600' : 'bg-gray-300'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
        ${checked ? 'translate-x-6' : 'translate-x-1'}`}
    />
  </button>
);

const NotificationPreferencesModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { user, updateUser } = useAuth();
  const [emailNotifications, setEmailNotifications] = useState(user?.emailNotifications !== false);
  const [notifyNewTask, setNotifyNewTask] = useState(user?.notifyNewTask !== false);
  const [notifyTaskUpdated, setNotifyTaskUpdated] = useState(user?.notifyTaskUpdated !== false);
  const [notifyTaskProgress, setNotifyTaskProgress] = useState(user?.notifyTaskProgress !== false);
  const [notifyTaskOverdue, setNotifyTaskOverdue] = useState(user?.notifyTaskOverdue !== false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const types = [
    { key: 'new', label: 'Nouvelle tâche', desc: 'Quand une tâche est créée dans une procédure suivie.', value: notifyNewTask, set: setNotifyNewTask },
    { key: 'upd', label: 'Tâche modifiée', desc: 'Quand une tâche est mise à jour.', value: notifyTaskUpdated, set: setNotifyTaskUpdated },
    { key: 'prg', label: 'Avancement de tâche', desc: 'Quand l’état d’une tâche change (en cours, terminée…).', value: notifyTaskProgress, set: setNotifyTaskProgress },
    { key: 'ovd', label: 'Tâche en retard', desc: 'Rappel quotidien des tâches dont l’échéance est dépassée.', value: notifyTaskOverdue, set: setNotifyTaskOverdue },
  ];

  const handleSave = async () => {
    setError('');
    setIsLoading(true);
    try {
      const updated = await apiService.updateNotificationPreferences({
        emailNotifications,
        notifyNewTask,
        notifyTaskUpdated,
        notifyTaskProgress,
        notifyTaskOverdue,
      });
      updateUser(updated);
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Une erreur est survenue.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                <BellIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-white">Préférences de notification</h2>
                <p className="text-xs text-primary-100">Choisissez ce que vous recevez</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <XMarkIcon className="h-4 w-4 text-white" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {success ? (
            <div className="text-center py-6">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="font-semibold text-gray-800 mb-1">Préférences enregistrées !</p>
              <p className="text-sm text-gray-500 mb-5">Vos choix ont été mis à jour.</p>
              <button
                onClick={onClose}
                className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-xl transition-colors"
              >
                Fermer
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              {/* Canal e-mail */}
              <div className="flex items-start justify-between gap-4 bg-gray-50 rounded-xl px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">Recevoir les e-mails</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Les notifications restent visibles dans l’application même si désactivé.
                  </p>
                </div>
                <Toggle checked={emailNotifications} onChange={setEmailNotifications} />
              </div>

              {/* Types */}
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                  Types de notifications
                </p>
                <div className="space-y-1">
                  {types.map((t) => (
                    <div key={t.key} className="flex items-start justify-between gap-4 px-1 py-2.5">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{t.label}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{t.desc}</p>
                      </div>
                      <Toggle checked={t.value} onChange={t.set} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-2.5 px-4 border border-gray-300 rounded-xl text-sm font-medium
                    text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isLoading}
                  className="flex-1 py-2.5 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-xl
                    text-sm font-medium disabled:opacity-50 transition-colors flex justify-center items-center gap-2"
                >
                  {isLoading ? (
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    'Enregistrer'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   Avatar initials helper
───────────────────────────────────────────────────────── */
function getAvatarColor(role?: string) {
  switch (role) {
    case 'SUPER_ADMIN': return 'bg-red-500';
    case 'RESPONSABLE': return 'bg-blue-500';
    default: return 'bg-primary-600';
  }
}

function getRoleBadge(role?: string): { label: string; cls: string } {
  switch (role) {
    case 'SUPER_ADMIN': return { label: 'Super Admin', cls: 'bg-red-100 text-red-700' };
    case 'RESPONSABLE': return { label: 'Responsable', cls: 'bg-blue-100 text-blue-700' };
    default: return { label: 'Utilisateur', cls: 'bg-gray-100 text-gray-600' };
  }
}

function initials(prenom?: string, nom?: string) {
  return `${(prenom || '').charAt(0)}${(nom || '').charAt(0)}`.toUpperCase() || 'U';
}

/* ─────────────────────────────────────────────────────────
   Breadcrumb helper
───────────────────────────────────────────────────────── */
function useBreadcrumb(pathname: string): string[] {
  const map: Record<string, string> = {
    '/': 'Tableau de bord',
    '/procedures': 'Procédures',
    '/structures': 'Structures',
    '/utilisateurs': 'Utilisateurs',
    '/statistiques': 'Statistiques',
    '/analytique': 'Analytique',
    '/admin': 'Administration',
    '/admin/phases': 'Phases & Pondération',
    '/admin/communication': 'Communication',
  };
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return ['Tableau de bord'];
  const crumbs: string[] = [];
  let path = '';
  for (const seg of segments) {
    path += `/${seg}`;
    const label = map[path];
    if (label) {
      crumbs.push(label);
    } else {
      // Detail page — show parent label if available
      const parentPath = path.substring(0, path.lastIndexOf('/'));
      const parentLabel = map[parentPath];
      if (parentLabel && !crumbs.includes(parentLabel)) crumbs.push(parentLabel);
      crumbs.push('Détail');
    }
  }
  return crumbs;
}

/* ─────────────────────────────────────────────────────────
   Notification helpers
───────────────────────────────────────────────────────── */
function notifIconBg(type: string): string {
  switch (type) {
    case 'NOUVELLE_TACHE':   return 'bg-blue-100 text-blue-600';
    case 'AVANCEMENT_TACHE': return 'bg-emerald-100 text-emerald-600';
    case 'TACHE_EN_RETARD':  return 'bg-red-100 text-red-600';
    default:                  return 'bg-gray-100 text-gray-500';
  }
}

function notifIcon(type: string): React.ReactNode {
  switch (type) {
    case 'NOUVELLE_TACHE':
      return (
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      );
    case 'AVANCEMENT_TACHE':
      return (
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'TACHE_EN_RETARD':
      return (
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
      );
    default:
      return (
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      );
  }
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `il y a ${hrs}h`;
  return `il y a ${Math.floor(hrs / 24)}j`;
}

/* ─────────────────────────────────────────────────────────
   NavItem
───────────────────────────────────────────────────────── */
interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  iconSolid: React.ElementType;
}

const SidebarNavItem: React.FC<{ item: NavItem; active: boolean; collapsed: boolean; onClick?: () => void }> = ({
  item,
  active,
  collapsed,
  onClick,
}) => {
  const Icon = active ? item.iconSolid : item.icon;
  return (
    <Link
      to={item.href}
      onClick={onClick}
      title={collapsed ? item.name : undefined}
      className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
        transition-all duration-150 select-none
        ${active
          ? 'bg-primary-50 text-primary-700'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }`}
    >
      {/* Active bar */}
      {active && (
        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-primary-600 rounded-r-full" />
      )}
      <Icon className={`h-5 w-5 flex-shrink-0 transition-colors ${active ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
      {!collapsed && <span className="truncate">{item.name}</span>}
    </Link>
  );
};

/* ─────────────────────────────────────────────────────────
   Layout
───────────────────────────────────────────────────────── */
const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout, isSuperAdmin, isResponsable } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // desktop collapsed
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showNotifPrefs, setShowNotifPrefs] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  // Clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  // Poll unread count every 30s
  const fetchUnreadCount = useCallback(async () => {
    try {
      const count = await apiService.getUnreadNotificationCount();
      setUnreadCount(count);
    } catch {
      // silent — non-blocking
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30_000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const openNotifDropdown = async () => {
    if (showNotifDropdown) { setShowNotifDropdown(false); return; }
    setShowNotifDropdown(true);
    setNotifLoading(true);
    try {
      const page = await apiService.getNotifications(0, 10);
      setNotifications(page.content);
    } catch {
      // silent
    } finally {
      setNotifLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    await apiService.markAllNotificationsAsRead();
    setUnreadCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleNotifClick = async (notif: AppNotification) => {
    if (!notif.read) {
      await apiService.markNotificationAsRead(notif.id);
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
    }
    setShowNotifDropdown(false);
    if (notif.procedureId) {
      navigate(`/procedures/${notif.procedureId}`);
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowUserDropdown(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const navigation: NavItem[] = [
    { name: 'Tableau de bord', href: '/', icon: HomeIcon, iconSolid: HomeIconSolid },
    { name: 'Procédures', href: '/procedures', icon: DocumentTextIcon, iconSolid: DocumentTextIconSolid },
    ...(isSuperAdmin()
      ? [{ name: 'Structures', href: '/structures', icon: BuildingOfficeIcon, iconSolid: BuildingOfficeIconSolid }]
      : []),
    ...(isSuperAdmin() || isResponsable()
      ? [{ name: 'Utilisateurs', href: '/utilisateurs', icon: UsersIcon, iconSolid: UsersIconSolid }]
      : []),
    { name: 'Statistiques', href: '/statistiques', icon: ChartBarIcon, iconSolid: ChartBarIconSolid },
    ...(isSuperAdmin()
      ? [{ name: 'Analytique', href: '/analytique', icon: PresentationChartLineIcon, iconSolid: PresentationChartLineIconSolid }]
      : []),
    ...(isSuperAdmin()
      ? [{ name: 'Phases & Pondération', href: '/admin/phases', icon: AdjustmentsHorizontalIcon, iconSolid: AdjustmentsHorizontalIconSolid }]
      : []),
    ...(isSuperAdmin()
      ? [{ name: 'Communication', href: '/admin/communication', icon: EnvelopeIcon, iconSolid: EnvelopeIconSolid }]
      : []),
  ];

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const breadcrumbs = useBreadcrumb(location.pathname);
  const badge = getRoleBadge(user?.role);
  const avatarCls = getAvatarColor(user?.role);

  const formattedDate = currentTime.toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const formattedTime = currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  /* ── Desktop sidebar width ── */
  const desktopSidebarW = sidebarCollapsed ? 'lg:w-[72px]' : 'lg:w-64';
  const desktopContentPl = sidebarCollapsed ? 'lg:pl-[72px]' : 'lg:pl-64';

  /* ── Sidebar inner content shared between mobile & desktop ── */
  const SidebarContent = ({ collapsed, onNavClick }: { collapsed: boolean; onNavClick?: () => void }) => (
    <>
      {/* Logo */}
      <div className={`flex h-16 items-center border-b border-gray-100 flex-shrink-0 ${collapsed ? 'justify-center px-3' : 'px-5 gap-3'}`}>
        <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm overflow-hidden border border-gray-100">
          <img src="/armoirie.jpeg" alt="Armoiries du Burkina Faso" className="w-full h-full object-contain p-0.5" />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 leading-tight truncate">Suivi Démat</p>
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">MTDPCE</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2.5 py-4 space-y-0.5">
        {navigation.map((item) => (
          <SidebarNavItem
            key={item.name}
            item={item}
            active={isActive(item.href)}
            collapsed={collapsed}
            onClick={onNavClick}
          />
        ))}
      </nav>

      {/* User section */}
      <div className={`flex-shrink-0 border-t border-gray-100 ${collapsed ? 'p-2.5' : 'p-4'}`}>
        {!collapsed ? (
          <>
            {/* Avatar + name */}
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-xl ${avatarCls} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                <span className="text-white text-sm font-semibold">
                  {initials(user?.prenom, user?.nom)}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {user?.prenom} {user?.nom}
                </p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${badge.cls}`}>
                  {badge.label}
                </span>
              </div>
            </div>
            {user?.structureNom && (
              <p className="text-[11px] text-gray-400 mb-3 truncate flex items-center gap-1">
                <BuildingOfficeIcon className="h-3 w-3 flex-shrink-0" />
                {user.structureNom}
              </p>
            )}
            <button
              onClick={() => setShowChangePassword(true)}
              className="flex items-center w-full gap-2.5 px-3 py-2 text-xs font-medium text-gray-500
                hover:bg-gray-100 hover:text-gray-700 rounded-lg transition-colors mb-1"
            >
              <KeyIcon className="h-3.5 w-3.5 flex-shrink-0" />
              Changer le mot de passe
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center w-full gap-2.5 px-3 py-2 text-xs font-medium text-red-500
                hover:bg-red-50 hover:text-red-700 rounded-lg transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-3.5 w-3.5 flex-shrink-0" />
              Déconnexion
            </button>
          </>
        ) : (
          /* Collapsed: just avatar */
          <div className={`w-9 h-9 rounded-xl ${avatarCls} flex items-center justify-center mx-auto shadow-sm cursor-default`} title={`${user?.prenom} ${user?.nom}`}>
            <span className="text-white text-xs font-semibold">{initials(user?.prenom, user?.nom)}</span>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}
      {showNotifPrefs && <NotificationPreferencesModal onClose={() => setShowNotifPrefs(false)} />}

      {/* ── Mobile overlay drawer ── */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          {/* Drawer */}
          <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white shadow-2xl">
            {/* Close btn */}
            <button
              className="absolute top-4 right-4 z-10 w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-4 w-4 text-gray-600" />
            </button>
            <SidebarContent collapsed={false} onNavClick={() => setSidebarOpen(false)} />
          </div>
        </div>
      )}

      {/* ── Desktop sidebar ── */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-30 lg:flex lg:flex-col bg-white border-r border-gray-100 shadow-sm transition-all duration-200 ${desktopSidebarW}`}>
        {/* Collapse toggle */}
        <button
          onClick={() => setSidebarCollapsed((v) => !v)}
          className="absolute -right-3 top-20 z-10 w-6 h-6 bg-white border border-gray-200 rounded-full shadow-sm
            flex items-center justify-center hover:bg-gray-50 transition-colors"
          title={sidebarCollapsed ? 'Agrandir' : 'Réduire'}
        >
          <ChevronRightIcon className={`h-3 w-3 text-gray-400 transition-transform duration-200 ${sidebarCollapsed ? '' : 'rotate-180'}`} />
        </button>
        <SidebarContent collapsed={sidebarCollapsed} />
      </div>

      {/* ── Main content ── */}
      <div className={`transition-all duration-200 ${desktopContentPl}`}>
        {/* ── Topbar ── */}
        <header className="sticky top-0 z-20 flex h-16 items-center gap-4 border-b border-gray-200 bg-white/95 backdrop-blur-md px-4 lg:px-6 shadow-sm">
          {/* Mobile hamburger */}
          <button
            type="button"
            className="lg:hidden -m-2 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-5 w-5" />
          </button>

          {/* Breadcrumb */}
          <nav className="hidden lg:flex items-center gap-1.5 text-sm min-w-0 flex-1">
            {breadcrumbs.map((crumb, i) => (
              <React.Fragment key={i}>
                {i > 0 && <ChevronRightIcon className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />}
                <span className={i === breadcrumbs.length - 1 ? 'font-semibold text-gray-800 truncate' : 'text-gray-400 hover:text-gray-600 truncate'}>
                  {crumb}
                </span>
              </React.Fragment>
            ))}
          </nav>

          {/* Mobile: title */}
          <span className="lg:hidden text-sm font-semibold text-gray-800 flex-1 truncate">
            {breadcrumbs[breadcrumbs.length - 1]}
          </span>

          {/* Right side */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Date/time */}
            <div className="hidden md:flex flex-col items-end leading-tight">
              <span className="text-xs font-medium text-gray-700">{formattedTime}</span>
              <span className="text-[10px] text-gray-400 capitalize">{formattedDate}</span>
            </div>

            {/* Divider */}
            <div className="hidden md:block w-px h-6 bg-gray-200" />

            {/* Notification bell */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={openNotifDropdown}
                className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Notifications"
              >
                <BellIcon className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 min-w-[16px] h-4 bg-red-500 rounded-full border-2 border-white flex items-center justify-center">
                    <span className="text-[9px] text-white font-bold leading-none px-0.5">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  </span>
                )}
              </button>

              {showNotifDropdown && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <span className="text-sm font-bold text-gray-900">Notifications</span>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Tout marquer comme lu
                      </button>
                    )}
                  </div>

                  {/* List */}
                  <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
                    {notifLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="w-5 h-5 border-2 border-primary-100 border-t-primary-500 rounded-full animate-spin" />
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-10 gap-2 text-gray-400">
                        <BellIcon className="h-8 w-8" />
                        <span className="text-sm">Aucune notification</span>
                      </div>
                    ) : (
                      notifications.map(notif => (
                        <button
                          key={notif.id}
                          onClick={() => handleNotifClick(notif)}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-start gap-3 ${notif.read ? '' : 'bg-primary-50/40'}`}
                        >
                          {/* Type icon */}
                          <span className={`flex-shrink-0 mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center ${notifIconBg(notif.type)}`}>
                            {notifIcon(notif.type)}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-semibold leading-tight ${notif.read ? 'text-gray-700' : 'text-gray-900'}`}>
                              {notif.title}
                            </p>
                            {notif.procedureNom && (
                              <p className="text-[10px] text-gray-400 mt-0.5 truncate">{notif.procedureNom}</p>
                            )}
                            <p className="text-[10px] text-gray-400 mt-0.5">{relativeTime(notif.createdAt)}</p>
                          </div>
                          {!notif.read && (
                            <span className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full mt-1.5" />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User avatar dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowUserDropdown((v) => !v)}
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className={`w-8 h-8 rounded-lg ${avatarCls} flex items-center justify-center shadow-sm`}>
                  <span className="text-white text-xs font-semibold">{initials(user?.prenom, user?.nom)}</span>
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-xs font-semibold text-gray-800 leading-tight">{user?.prenom} {user?.nom}</p>
                  <p className="text-[10px] text-gray-400 leading-tight">{badge.label}</p>
                </div>
                <ChevronDownIcon className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-150 ${showUserDropdown ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown */}
              {showUserDropdown && (
                <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-xl border border-gray-100 py-1.5 z-50 overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900">{user?.prenom} {user?.nom}</p>
                    <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={() => { setShowNotifPrefs(true); setShowUserDropdown(false); }}
                    className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <BellIcon className="h-4 w-4 text-gray-400" />
                    Préférences de notification
                  </button>
                  <button
                    onClick={() => { setShowChangePassword(true); setShowUserDropdown(false); }}
                    className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <KeyIcon className="h-4 w-4 text-gray-400" />
                    Changer le mot de passe
                  </button>
                  <div className="border-t border-gray-100 mt-1 pt-1">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4" />
                      Déconnexion
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="py-6 px-4 lg:px-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
